import { useApolloClient } from '@apollo/client';
import {
  ConversationChannelType,
  ConversationMessageDispatchStatus,
  ConversationParticipantEntity,
  ConversationSmsDirection,
} from '@provision/conversation-types';
import { ConversationParticipation } from '@provision/entity-types';
import { toNationalPhoneDigits } from '@provision/formatting-utils';
import { useMutation, useQuery } from '@provision/graphql-client';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { config } from 'src/core/config';
import { wsEventBus } from 'src/core/realtime-connection';
import { ConversationParticipantItem } from 'src/graphql';
import { usePhonesAndEmailsLabels } from 'src/graphql/entities/client-label';
import { DEFAULT_MESSAGE_LIMIT } from 'src/graphql/entities/conversation-message/constants';
import { GET_CONVERSATION_MESSAGES_QUERY } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import { getConversationMessagesQueryVariables } from 'src/graphql/entities/conversation-message/hooks/use-conversation-messages';
import type { QueryVariables } from 'src/graphql/types';
import {
  getEffectiveDispatchStatus,
  parseDispatchFailureReason,
} from 'src/pages/Conversations/Chat/ChatMessage/dispatch-status/dispatch-status.utils';
import { useSmsSwitchVisibility } from 'src/pages/Conversations/Chat/InputField/hooks/use-sms-switch-visibility';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import type { SmsChannelInfo } from './sms-recipients';
import {
  SmsSessionContext,
  type SmsSessionContextValue,
  type SmsSessionRecipients,
  type StartedSmsParticipation,
} from './sms-session-context';
import { groupMessagesBySession, type TaggedMessage } from './sms-session-grouping';
import {
  END_SMS_SESSION_MUTATION,
  type EndSmsSessionMutationResult,
  type EndSmsSessionMutationVars,
  GET_OPEN_SMS_SESSION_QUERY,
  GET_SMS_SESSIONS_WITH_RECIPIENTS_QUERY,
  getOpenSmsSessionQueryVariables,
  type GetSmsSessionsWithRecipientsQuery,
  getSmsSessionsWithRecipientsQueryVariables,
  type SmsSessionRecipientInput,
  START_SMS_SESSION_MUTATION,
  type StartSmsSessionMutationResult,
  type StartSmsSessionMutationVars,
} from './sms-session.operations';
import { patchSessionOpen, useSmsSessionRealtime } from './use-sms-session-realtime';

interface RawMessageRecord {
  conversationMessageId: string;
  createdAt: string;
  body?: string | null;
  partnerId?: number | null;
  senderChannel?: { id: string } | null;
  attachments?: { fileName?: string | null; fileKey?: string | null }[] | null;
  messageDispatches?:
    | {
        dispatchStatus?: string | null;
        customAttributes?: string | null;
        createdAt?: string | null;
        updatedAt?: string | null;
      }[]
    | null;
  conversationSmsRecords?:
    | {
        conversationSmsSessionId: string;
        direction: string;
        externalMessageId: string | null;
      }[]
    | null;
}

interface RawQueryData {
  getConversationMessage: {
    records: RawMessageRecord[] | null;
    metadata: { offset: number; totalRecords: number };
  };
}

function useTaggedMessages(
  conversationId: string,
  skip: boolean,
): { messages: TaggedMessage[]; loading: boolean } {
  const variables = useMemo(
    () =>
      getConversationMessagesQueryVariables({
        filters: [{ attribute: 'conversationId', operator: '=', value: conversationId }],
        limit: DEFAULT_MESSAGE_LIMIT,
      }),
    [conversationId],
  );

  const { data, loading } = useQuery<RawQueryData>(GET_CONVERSATION_MESSAGES_QUERY, {
    variables,
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: skip || !conversationId,
  });

  const messages = useMemo<TaggedMessage[]>(() => {
    const records = data?.getConversationMessage?.records ?? [];
    return records
      .filter((msg) => msg.conversationMessageId && msg.createdAt)
      .map((msg) => {
        const smsRecords = msg.conversationSmsRecords ?? [];
        // A wire id on the OUTGOING row means Twilio accepted the message: a dispatch still
        // `dispatching` next to it is stale bookkeeping, not a lost send. An explicit `failed` row always counts.
        const acceptedByTwilio = smsRecords.some(
          (sms) =>
            (sms.direction as ConversationSmsDirection) ===
              ConversationSmsDirection.Outgoing && Boolean(sms.externalMessageId),
        );
        const failedDispatch = (msg.messageDispatches ?? []).find((dispatch) => {
          if (
            (dispatch.dispatchStatus as ConversationMessageDispatchStatus) ===
            ConversationMessageDispatchStatus.Failed
          ) {
            return true;
          }
          return (
            !acceptedByTwilio &&
            getEffectiveDispatchStatus({
              dispatchStatus: dispatch.dispatchStatus ?? '',
              createdAt: dispatch.createdAt ?? msg.createdAt,
              updatedAt: dispatch.updatedAt ?? '',
            }) === ConversationMessageDispatchStatus.Failed
          );
        });

        return {
          conversationMessageId: msg.conversationMessageId,
          createdAt: msg.createdAt,
          body: msg.body ?? null,
          senderChannelId: msg.senderChannel?.id ?? null,
          attachments: (msg.attachments ?? [])
            .filter((attachment) => attachment?.fileName)
            .map((attachment) => ({
              fileName: attachment.fileName!,
              url:
                attachment.fileKey && msg.partnerId
                  ? `${config.s3AttachmentsUrl}${msg.partnerId}/${conversationId}/${attachment.fileKey}`
                  : null,
            })),
          dispatchFailed: Boolean(failedDispatch),
          dispatchFailureReason: failedDispatch
            ? parseDispatchFailureReason(failedDispatch.customAttributes)
            : null,
          smsTags: smsRecords.map((sms) => ({
            conversationSmsSessionId: sms.conversationSmsSessionId,
            direction: sms.direction,
          })),
        };
      });
  }, [data, conversationId]);

  return { messages, loading };
}

interface SmsSessionProviderProps {
  conversationId: string;
  participants: ConversationParticipantItem[];
  isLoadingChannels: boolean;
  children: React.ReactNode;
}

function buildSendersByChannelId(
  participants: ConversationParticipantItem[],
  participations: ConversationParticipation[],
): ReadonlyMap<string, string> {
  const namesByParticipantId = new Map<string, string>();
  for (const participant of participants) {
    const fullName = participant.client?.fullName ?? participant.platformUser?.fullName;
    if (participant.conversationParticipantId && fullName) {
      namesByParticipantId.set(participant.conversationParticipantId, fullName);
    }
  }

  const map = new Map<string, string>();
  for (const participation of participations) {
    const channel = participation.channel;
    const name = channel?.conversationParticipantId
      ? namesByParticipantId.get(channel.conversationParticipantId)
      : undefined;
    if (channel?.id && name) map.set(channel.id, name);
  }
  return map;
}

/** TwilioSMS channels with the person behind each, oldest first; the entity layer stores null as '' */
function buildSmsChannelsById({
  participants,
  participations,
  sendersByChannelId,
  labels,
}: {
  participants: ConversationParticipantItem[];
  participations: ConversationParticipation[];
  sendersByChannelId: ReadonlyMap<string, string>;
  labels: { id: number; label: string }[];
}): ReadonlyMap<string, SmsChannelInfo> {
  // a channel's number may be a labelled row of someone on the conversation: the thread says "Casi · Work"
  const labelByDigits = new Map<string, string>();
  for (const participant of participants) {
    for (const row of participant.client?.phoneNumbers ?? []) {
      const rowDigits =
        row.number === undefined ? null : toNationalPhoneDigits(String(row.number));
      const label = labels.find((candidate) => candidate.id === row.labelId)?.label;
      if (rowDigits && label && !labelByDigits.has(rowDigits)) {
        labelByDigits.set(rowDigits, label);
      }
    }
  }
  const participantById = new Map(
    participants.map((participant) => [participant.conversationParticipantId, participant]),
  );
  const channels = participations
    .filter(
      (p) =>
        p.isActive &&
        (p.channel.conversationChannelTypeId as ConversationChannelType) ===
          ConversationChannelType.TwilioSMS,
    )
    .sort((a, b) => (a.channel.createdAt ?? '').localeCompare(b.channel.createdAt ?? ''));

  const map = new Map<string, SmsChannelInfo>();
  for (const { channel } of channels) {
    const digits = toNationalPhoneDigits(channel.externalIdentifier);
    if (!digits) continue;
    const participant = participantById.get(channel.conversationParticipantId);
    map.set(channel.id, {
      conversationChannelId: channel.id,
      participantId: channel.conversationParticipantId,
      name: sendersByChannelId.get(channel.id) ?? null,
      label: labelByDigits.get(digits) ?? null,
      digits,
      internalIdentifier: channel.internalIdentifier,
      entity:
        (participant?.participantEntity as ConversationParticipantEntity | undefined) ??
        ConversationParticipantEntity.Unknown,
      identity: participant?.participantIdentity ?? digits,
      lineType: channel.lineType || null,
      lineTypeCheckedAt: channel.lineTypeCheckedAt || null,
    });
  }
  return map;
}

export const SmsSessionProvider = ({
  conversationId,
  participants,
  isLoadingChannels,
  children,
}: SmsSessionProviderProps): React.JSX.Element => {
  const client = useApolloClient();

  const conversationParticipations = useChatCommonData((s) => s.conversationParticipations);
  const sendersByChannelId = useMemo(
    () => buildSendersByChannelId(participants, conversationParticipations),
    [participants, conversationParticipations],
  );
  const { clientPhoneLabels } = usePhonesAndEmailsLabels();
  const smsChannelsById = useMemo(
    () =>
      buildSmsChannelsById({
        participants,
        participations: conversationParticipations,
        sendersByChannelId,
        labels: clientPhoneLabels ?? [],
      }),
    [participants, conversationParticipations, sendersByChannelId, clientPhoneLabels],
  );

  const {
    showSmsSwitch,
    hasOpenSession,
    openSessionId,
    sessionOpenedAt: openedAt,
    smsDisabledReason,
  } = useSmsSwitchVisibility(conversationId, smsChannelsById.values().next().value);

  const { data: sessionsData } = useQuery<GetSmsSessionsWithRecipientsQuery, QueryVariables>(
    GET_SMS_SESSIONS_WITH_RECIPIENTS_QUERY,
    {
      variables: getSmsSessionsWithRecipientsQueryVariables(conversationId),
      // ttl 0: the TTL link would answer from cache and miss sessions opened while off screen
      fetchPolicy: 'cache-and-network',
      context: { ttl: 0 },
      skip: !showSmsSwitch || !conversationId,
    },
  );
  const sessionsById = useMemo(() => {
    const map = new Map<string, SmsSessionRecipients>();
    for (const record of sessionsData?.getConversationSmsSession?.records ?? []) {
      map.set(record.conversationSmsSessionId, {
        recipientChannelIds: (record.recipients ?? [])
          .filter((recipient) => !recipient.deletedAt)
          .map((recipient) => recipient.conversationChannelId),
        partnerPhoneNumber: record.partnerPhoneNumber || null,
      });
    }
    return map;
  }, [sessionsData]);

  const [endSmsMutation, { loading: isEnding }] = useMutation<
    EndSmsSessionMutationResult,
    EndSmsSessionMutationVars
  >(END_SMS_SESSION_MUTATION);

  const [startSmsMutation] = useMutation<
    StartSmsSessionMutationResult,
    StartSmsSessionMutationVars
  >(START_SMS_SESSION_MUTATION);

  const startSession = useCallback(
    async (recipients: SmsSessionRecipientInput[]): Promise<StartedSmsParticipation[]> => {
      const { data } = await startSmsMutation({
        variables: { input: { conversationId, recipients } },
        // patch and refetch now: a late or missed WS event must not leave the panel and lock empty
        onCompleted(result) {
          patchSessionOpen(
            client,
            result.startSmsSession.conversationSmsSession,
            conversationId,
          );
          void client.refetchQueries({ include: [GET_SMS_SESSIONS_WITH_RECIPIENTS_QUERY] });
        },
      });
      return data?.startSmsSession.smsParticipations ?? [];
    },
    [startSmsMutation, client, conversationId],
  );

  const endSession = useCallback(async () => {
    if (!openSessionId) return;
    await endSmsMutation({
      variables: { input: { conversationSmsSessionId: openSessionId } },
      onCompleted() {
        client.writeQuery({
          query: GET_OPEN_SMS_SESSION_QUERY,
          variables: getOpenSmsSessionQueryVariables(conversationId),
          data: { getConversationSmsSession: { records: [] } },
        });
      },
    });
  }, [openSessionId, endSmsMutation, client, conversationId]);

  const [overlayMessages, setOverlayMessages] = useState<TaggedMessage[]>([]);
  const prevConversationIdRef = useRef(conversationId);
  if (prevConversationIdRef.current !== conversationId) {
    prevConversationIdRef.current = conversationId;
    setOverlayMessages([]);
  }

  const handlePairComplete = useCallback((msg: TaggedMessage) => {
    setOverlayMessages((prev) => {
      if (prev.some((m) => m.conversationMessageId === msg.conversationMessageId)) {
        return prev;
      }
      return [...prev, msg];
    });
  }, []);

  useSmsSessionRealtime({
    client,
    wsEventBus,
    conversationId,
    showSmsSwitch,
    onPairComplete: handlePairComplete,
  });

  const { messages: cacheMessages, loading: messagesLoading } = useTaggedMessages(
    conversationId,
    !showSmsSwitch,
  );

  const mergedMessages = useMemo<TaggedMessage[]>(() => {
    if (overlayMessages.length === 0) return cacheMessages;

    const overlayById = new Map(overlayMessages.map((m) => [m.conversationMessageId, m]));

    const tagged = cacheMessages.map((m) => {
      const overlay = overlayById.get(m.conversationMessageId);
      return overlay && m.smsTags.length === 0 ? { ...m, smsTags: overlay.smsTags } : m;
    });

    const cacheIds = new Set(cacheMessages.map((m) => m.conversationMessageId));

    const newOverlay = overlayMessages.filter((m) => !cacheIds.has(m.conversationMessageId));

    return newOverlay.length === 0 ? tagged : [...tagged, ...newOverlay];
  }, [cacheMessages, overlayMessages]);

  const sessionGroups = useMemo(
    () => groupMessagesBySession(mergedMessages, openSessionId),
    [mergedMessages, openSessionId],
  );

  const value = useMemo<SmsSessionContextValue>(
    () => ({
      openSessionId,
      openedAt,
      sessionGroups,
      sessionsById,
      smsChannelsById,
      isLoadingChannels,
      isLoading: messagesLoading,
      startSession,
      endSession,
      isEnding,
      showSmsSwitch,
      hasOpenSession,
      smsDisabledReason,
      sendersByChannelId,
    }),
    [
      showSmsSwitch,
      hasOpenSession,
      smsDisabledReason,
      openSessionId,
      openedAt,
      sessionGroups,
      sessionsById,
      smsChannelsById,
      isLoadingChannels,
      messagesLoading,
      startSession,
      endSession,
      isEnding,
      sendersByChannelId,
    ],
  );

  return <SmsSessionContext.Provider value={value}>{children}</SmsSessionContext.Provider>;
};
